
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { finanzasService } from '../services/api';
import '../styles/Finanzas.css';

const Finanzas = () => {
  const { codigo } = useParams();
  const tenantCodigo = codigo || localStorage.getItem('tenant_codigo');
  const todayISO = new Date().toISOString().split('T')[0];
  const [dashboard, setDashboard] = useState({
    gastos_mes: 0,
    gastos_pendientes: 0,
    balance_bancos: 0,
    balance_caja_chica: 0,
    total_cobrado: 0,
    top_tipos_gastos: [],
    total_capital_prestado: 0,
    total_interes_generado: 0,
    total_capital_pagado: 0,
    total_interes_pagado: 0,
    total_capital_pendiente: 0,
    total_interes_pendiente: 0,
    fecha_inicio: todayISO,
    fecha_fin: todayISO
  });
  const [loading, setLoading] = useState(true);
  const [fechaInicio, setFechaInicio] = useState(todayISO);
  const [fechaFin, setFechaFin] = useState(todayISO);

  useEffect(() => {
    setDashboard(prev => ({
      ...prev,
      gastos_mes: 0,
      gastos_pendientes: 0,
      balance_bancos: 0,
      balance_caja_chica: 0,
      total_cobrado: 0,
      total_capital_prestado: 0,
      total_interes_generado: 0,
      total_capital_pagado: 0,
      total_interes_pagado: 0,
      total_capital_pendiente: 0,
      total_interes_pendiente: 0,
      top_tipos_gastos: []
    }));
    setFechaInicio(todayISO);
    setFechaFin(todayISO);
    cargarDashboard({ fecha_inicio: todayISO, fecha_fin: todayISO });
  }, [tenantCodigo, todayISO]);

  const cargarDashboard = async (filtros = {}) => {
    try {
      setLoading(true);
      const response = await finanzasService.getDashboardFinanzas(filtros);
      setDashboard(prev => ({
        ...prev,
        ...(response.data || {}),
        top_tipos_gastos: (response.data?.top_tipos_gastos || response.top_tipos_gastos || []),
        fecha_inicio: response.data?.fecha_inicio || response.fecha_inicio || filtros.fecha_inicio || prev.fecha_inicio,
        fecha_fin: response.data?.fecha_fin || response.fecha_fin || filtros.fecha_fin || prev.fecha_fin
      }));
    } catch (error) {
      console.error('Error al cargar dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    const number = Number(value) || 0;
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: 'DOP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(number);
  };

  const formatDisplayDate = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString('es-DO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const periodoLabel = dashboard.fecha_inicio && dashboard.fecha_fin
    ? dashboard.fecha_inicio === dashboard.fecha_fin
      ? formatDisplayDate(dashboard.fecha_inicio)
      : `${formatDisplayDate(dashboard.fecha_inicio)} - ${formatDisplayDate(dashboard.fecha_fin)}`
    : null;

  const aplicarFiltro = () => {
    if (!fechaInicio || !fechaFin) return;
    cargarDashboard({ fecha_inicio: fechaInicio, fecha_fin: fechaFin });
  };

  const aplicarHoy = () => {
    setFechaInicio(todayISO);
    setFechaFin(todayISO);
    cargarDashboard({ fecha_inicio: todayISO, fecha_fin: todayISO });
  };

  const totalCobrado = dashboard.total_cobrado ?? dashboard.balance_caja_chica ?? 0;

  const buildRoute = (path) => {
    if (tenantCodigo) {
      return `/${tenantCodigo}${path}`;
    }
    return path;
  };

  const modulos = [
    { id: 'gastos', nombre: 'Gastos', icono: '💰', descripcion: 'Registro y control de gastos operativos', ruta: buildRoute('/finanzas/gastos') },
    { id: 'caja_chica', nombre: 'Caja Chica', icono: '💵', descripcion: 'Manejo de efectivo menor', ruta: buildRoute('/finanzas/caja-chica') },
    { id: 'cuadre_caja', nombre: 'Cuadre de Caja', icono: '📊', descripcion: 'Cierre y cuadre diario de operaciones', ruta: buildRoute('/finanzas/cuadre-caja') },
    { id: 'flujo_caja', nombre: 'Flujo de Caja', icono: '💹', descripcion: 'Movimientos de entrada y salida de efectivo', ruta: buildRoute('/finanzas/flujo-caja') },
    { id: 'cuentas_pagar', nombre: 'Cuentas por Pagar', icono: '📋', descripcion: 'Gestión de obligaciones pendientes', ruta: buildRoute('/finanzas/cuentas-pagar') },
    { id: 'facturas', nombre: 'Factura de Suplidores', icono: '🧾', descripcion: 'Gestión de facturas de proveedores', ruta: buildRoute('/finanzas/facturas') },
    { id: 'bancos', nombre: 'Bancos', icono: '🏦', descripcion: 'Manejo de cuentas bancarias', ruta: buildRoute('/finanzas/bancos') },
    { id: 'transacciones', nombre: 'Transacciones', icono: '🔄', descripcion: 'Registro de movimientos financieros', ruta: buildRoute('/finanzas/transacciones') },
    { id: 'tipos_egresos', nombre: 'Tipo de Egresos', icono: '📝', descripcion: 'Categorización de gastos', ruta: buildRoute('/finanzas/tipos-egresos') },
    { id: 'suplidores', nombre: 'Suplidores', icono: '🚚', descripcion: 'Gestión de proveedores', ruta: buildRoute('/finanzas/suplidores') }
  ];

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading">Cargando dashboard financiero...</div>
      </div>
    );
  }

  return (
    <div className="finanzas-container">
      <div className="finanzas-header">
        <h1>⚖️ Módulo de Finanzas</h1>
        <p>Sistema integral de gestión financiera y contabilidad</p>
      </div>

      {/* Dashboard de Resumen */}
      <div className="dashboard-section">
        <div className="section-header">
          <h2>📈 Resumen Financiero</h2>
          {periodoLabel && (
            <span className="range-label">
              Periodo: <strong>{periodoLabel}</strong>
            </span>
          )}
        </div>

        <div className="dashboard-controls">
          <div className="filter-group">
            <label htmlFor="filtro-fecha-inicio">Desde</label>
            <input
              id="filtro-fecha-inicio"
              type="date"
              value={fechaInicio}
              max={fechaFin}
              onChange={(e) => setFechaInicio(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label htmlFor="filtro-fecha-fin">Hasta</label>
            <input
              id="filtro-fecha-fin"
              type="date"
              value={fechaFin}
              min={fechaInicio}
              onChange={(e) => setFechaFin(e.target.value)}
            />
          </div>
          <div className="filter-actions">
            <button type="button" className="btn btn-apply" onClick={aplicarFiltro} disabled={loading}>
              Aplicar
            </button>
            <button type="button" className="btn btn-reset" onClick={aplicarHoy} disabled={loading}>
              Hoy
            </button>
          </div>
        </div>

        <div className="dashboard-cards">
          <div className="dashboard-card gastos">
            <div className="card-icon">💰</div>
            <div className="card-content">
              <h3>Gastos del Mes</h3>
              <p className="card-value">{formatCurrency(dashboard.gastos_mes)}</p>
            </div>
          </div>

          <div className="dashboard-card pendientes">
            <div className="card-icon">⏳</div>
            <div className="card-content">
              <h3>Gastos Pendientes</h3>
              <p className="card-value">{dashboard.gastos_pendientes}</p>
            </div>
          </div>

          <div className="dashboard-card bancos">
            <div className="card-icon">🏦</div>
            <div className="card-content">
              <h3>Balance Bancos</h3>
              <p className="card-value">{formatCurrency(dashboard.balance_bancos)}</p>
            </div>
          </div>

          <div className="dashboard-card metric total-cobrado">
            <div className="card-icon">💵</div>
            <div className="card-content">
              <h3>Total Cobrado</h3>
              <p className="card-value">{formatCurrency(totalCobrado)}</p>
            </div>
          </div>

          <div className="dashboard-card metric capital-prestado">
            <div className="card-icon">📦</div>
            <div className="card-content">
              <h3>Total Capital Prestado</h3>
              <p className="card-value">{formatCurrency(dashboard.total_capital_prestado)}</p>
            </div>
          </div>

          <div className="dashboard-card metric interes-generado">
            <div className="card-icon">📊</div>
            <div className="card-content">
              <h3>Total Interés Generado</h3>
              <p className="card-value">{formatCurrency(dashboard.total_interes_generado)}</p>
            </div>
          </div>

          <div className="dashboard-card metric capital-pagado">
            <div className="card-icon">✅</div>
            <div className="card-content">
              <h3>Capital Pagado</h3>
              <p className="card-value">{formatCurrency(dashboard.total_capital_pagado)}</p>
            </div>
          </div>

          <div className="dashboard-card metric interes-pagado">
            <div className="card-icon">💹</div>
            <div className="card-content">
              <h3>Interés Pagado</h3>
              <p className="card-value">{formatCurrency(dashboard.total_interes_pagado)}</p>
            </div>
          </div>

          <div className="dashboard-card metric capital-pendiente">
            <div className="card-icon">📉</div>
            <div className="card-content">
              <h3>Capital Pendiente</h3>
              <p className="card-value">{formatCurrency(dashboard.total_capital_pendiente)}</p>
            </div>
          </div>

          <div className="dashboard-card metric interes-pendiente">
            <div className="card-icon">🕒</div>
            <div className="card-content">
              <h3>Interés Pendiente</h3>
              <p className="card-value">{formatCurrency(dashboard.total_interes_pendiente)}</p>
            </div>
          </div>
        </div>

        {/* Top Gastos */}
        {dashboard.top_tipos_gastos.length > 0 && (
          <div className="top-gastos">
            <h3>🏆 Top 5 Categorías de Gastos</h3>
            <div className="gastos-list">
              {dashboard.top_tipos_gastos.map((item, index) => (
                <div key={index} className="gasto-item">
                  <span className="posicion">#{index + 1}</span>
                  <span className="categoria">{item.tipo}</span>
                  <span className="monto">{formatCurrency(item.total)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Módulos de Finanzas */}
      <div className="modulos-section">
        <h2>🔧 Módulos Disponibles</h2>
        <div className="modulos-grid">
          {modulos.map(modulo => (
            <div key={modulo.id} className="modulo-card">
              <div className="modulo-icono">{modulo.icono}</div>
              <div className="modulo-info">
                <h3>{modulo.nombre}</h3>
                <p>{modulo.descripcion}</p>
              </div>
              <div className="modulo-acciones">
                <Link to={modulo.ruta} className="btn-modulo activo">
                  Acceder
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Finanzas;